import { always, curryN, identity } from 'ramda';
import { isEmpty } from '@ringcentral/rwc-utils/src/functional';

/**
 * string -> ([{}], merge) -> {}
 * @param key {String}
 * @param list {Array}
 * @param merge {Function}
 */
export const classify = curryN(2, (key, list, merge = identity) =>
    list.reduce((st, item) => {
        st[item[key]] = merge(item, item[key], st);
        return st;
    }, {})
);

/**
 *
 * @param key {String}
 * @param base {Array} [{key, ...}...]
 * @param aux {Object}
 */
export const innerJoin = (key, base, aux, merge = identity) =>
    base.map(item => ({
        ...item,
        ...(aux.hasOwnProperty(item[key]) ? merge(aux[item[key]], item) : {}),
    }));

export const leftJoin = (key, first, second, merge, auxKey) =>
    innerJoin(key, first, classify(auxKey || key, second), merge);

/**
 * @param key {String}
 * @param base {Array} [{key, ...}...]
 * @param keep {String} 'last' | 'first'
 */
export const distinctBy = (key, arr, keep = 'first') =>
    Object.values(
        arr.reduce((result, item) => {
            if (!result[item[key]] || keep === 'last') {
                result[item[key]] = item;
            }

            return result;
        }, {})
    );

export const pass = () => void 0;
export const passPromise = () => Promise.resolve();

export const stubNull = always(null);

export const notEmpty = v => !isEmpty(v);

export const resolveInRT = foo => (typeof foo === 'function' ? foo() : foo);

export const counter = n => () => n++;

/**
 * Is used to apply per key transformation of given object.
 * Passes every value inside the 'source object' to the
 * corresponding (with same key) function within 'transformations' object.
 * Returns a new object containing transformed values.
 * @param {Object} source - source object
 * @param {Object} transformations - dicrionary with functions
 */
export const perKeyTransform = (source, transformations) => {
    const newMeta = {};

    Object.keys(source).forEach(key => {
        if (transformations[key]) {
            newMeta[key] = transformations[key](source[key]);
        } else {
            newMeta[key] = source[key];
        }
    });

    return newMeta;
};

/**
 * @returns string|undefined
 */
export const isAutotestMode = () => {
    if (sessionStorage.hasOwnProperty('isAutotestMode')) {
        return JSON.parse(sessionStorage.getItem('isAutotestMode'));
    }
};

/**
 * Checks if a number is in given numerical interval.
 * @param {number} from - inclusively
 * @param {number} to - exclusively
 * @returns {(n: number) => Boolean} function to check number
 */
export const isNumberBetween = curryN(3, (from, to, n) => from <= n && n < to);
